export interface VisibilityCondition {
  propertyToken: string;
  operator?: string;
  value?: string;
}

/** Every operator the switch below answers. The editor and its translations are held to this. */
export const VISIBILITY_OPERATORS = [
  'equals',
  'notEquals',
  'contains',
  'notContains',
  'greaterThan',
  'lessThan',
  'isEmpty',
  'isNotEmpty'
] as const;

/** These take no comparison value, so the editor hides the value field. */
export const VALUELESS_OPERATORS: readonly string[] = ['isEmpty', 'isNotEmpty'];

/**
 * Decides whether a property is shown, given the values entered so far.
 *
 * This logic also exists in `api/Properties/VisibilityEvaluator.cs` — the wizard
 * hides fields as the user types, and the server must not require a hidden field.
 * Both are held to `tests/fixtures/visibility-cases.json`; add a case there before
 * changing either.
 */
export function isVisible(
  condition: VisibilityCondition | null | undefined,
  values: Record<string, string>
): boolean {
  if (!condition || !condition.propertyToken.trim()) return true;

  const actual = lookup(values, condition.propertyToken);
  const expected = condition.value ?? '';

  switch (condition.operator ?? 'equals') {
    case 'equals':
      return equalsIgnoreCase(actual, expected);
    case 'notEquals':
      return !equalsIgnoreCase(actual, expected);
    case 'contains':
      return actual.toLowerCase().includes(expected.toLowerCase());
    case 'notContains':
      return !actual.toLowerCase().includes(expected.toLowerCase());
    case 'greaterThan':
      return compare(actual, expected) > 0;
    case 'lessThan':
      return compare(actual, expected) < 0;
    case 'isEmpty':
      return actual.length === 0;
    case 'isNotEmpty':
      return actual.length > 0;
    default:
      // An unrecognised operator must not hide a field the author meant to show.
      return true;
  }
}

export function parseVisibilityCondition(conditionJson: string | null | undefined): VisibilityCondition | null {
  if (!conditionJson?.trim()) return null;
  try {
    const parsed: unknown = JSON.parse(conditionJson);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed as VisibilityCondition;
  } catch {
    return null;
  }
}

function equalsIgnoreCase(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

function lookup(values: Record<string, string>, token: string): string {
  const direct = values[token];
  if (direct !== undefined) return direct;

  const match = Object.keys(values).find((key) => key.toLowerCase() === token.toLowerCase());
  return match === undefined ? '' : values[match];
}

/**
 * Returns 0 when either side is not a number, so a non-numeric value satisfies
 * neither greaterThan nor lessThan. Deliberately stricter than the legacy code,
 * where parseFloat("12abc") silently yielded 12.
 */
function compare(actual: string, expected: string): number {
  const left = strictNumber(actual);
  const right = strictNumber(expected);
  if (left === null || right === null) return 0;
  return left === right ? 0 : left - right;
}

function strictNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}
